import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { PROPERTY_SEARCH_ASSISTANT_PATH } from '@/lib/assistantRoutes';
import { clearAssistantChatState } from '@/lib/assistantChatPersistence';
import {
    findResumableSearch,
    type SearchResumeSummary,
} from '@/lib/searchResume';

export function useNavigateToPropertySearch() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [pendingSearch, setPendingSearch] = useState<SearchResumeSummary | null>(null);
    const [showResumeDialog, setShowResumeDialog] = useState(false);
    const [checking, setChecking] = useState(false);

    const goToFreshSearch = useCallback(() => {
        clearAssistantChatState(user?.id);
        clearAssistantChatState('guest');
        navigate(`${PROPERTY_SEARCH_ASSISTANT_PATH}?fresh=1`);
    }, [navigate, user?.id]);

    const goToPropertySearch = useCallback(async () => {
        setChecking(true);
        try {
            const match = await findResumableSearch(user?.id);
            if (match) {
                setPendingSearch(match);
                setShowResumeDialog(true);
                return;
            }
        } catch (e) {
            console.warn('Could not check recent searches:', e);
        } finally {
            setChecking(false);
        }
        goToFreshSearch();
    }, [goToFreshSearch, user?.id]);

    const continueSearch = useCallback(() => {
        setShowResumeDialog(false);
        const pending = pendingSearch;
        setPendingSearch(null);
        if (pending?.source === 'history' && pending.sessionId) {
            navigate(PROPERTY_SEARCH_ASSISTANT_PATH, {
                state: { searchResumeAction: 'continue', sessionId: pending.sessionId },
            });
            return;
        }
        navigate(PROPERTY_SEARCH_ASSISTANT_PATH);
    }, [navigate, pendingSearch]);

    const startNewSearch = useCallback(() => {
        setShowResumeDialog(false);
        setPendingSearch(null);
        goToFreshSearch();
    }, [goToFreshSearch]);

    return {
        goToPropertySearch,
        continueSearch,
        startNewSearch,
        pendingSearch,
        showResumeDialog,
        checking,
    };
}
